import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './HousePopup.css';
import {
  RANGE_OPTIONS,
  periodKpiLabel,
  computePerf,
  hasEnoughHistory,
  filterHistoryToRange,
  computeYDomain,
} from './popupShared';

// ─── formatting helpers ────────────────────────────────────────────────────────

function fmtEur(value, showSign = false) {
  if (value == null || isNaN(value)) return '—';
  const sign = showSign ? (value >= 0 ? '+' : '') : '';
  return `${sign}€${Math.round(Math.abs(value)).toLocaleString()}`;
}

function fmtGbp(value, dp = 0) {
  if (value == null || isNaN(value)) return '—';
  return `£${Number(value).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}

function fmtPct(value) {
  if (value == null || isNaN(value)) return '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function pnlClass(value) {
  if (value == null || isNaN(value)) return '';
  return value >= 0 ? 'house-pos' : 'house-neg';
}

function formatDateLong(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function monthsToYearsLabel(months) {
  if (months == null || isNaN(months)) return '—';
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (m === 0) return `${y} yr${y === 1 ? '' : 's'}`;
  return `${y} yr${y === 1 ? '' : 's'} ${m} mo`;
}

function houseFormatYAxis(value) {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000)     return `€${(value / 1_000).toFixed(0)}k`;
  return `€${value}`;
}

function houseFormatXAxis(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function HouseChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="house-chart-tooltip">
      <div className="house-chart-tooltip-date">{formatDateLong(label)}</div>
      <div>€{Math.round(payload[0]?.value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function HousePopup({ onClose, investment }) {
  const [range, setRange] = useState('1M');

  const detail = investment?.houseDetail || {};
  const property = detail.property || {};
  const mortgage = detail.mortgage || {};

  // FX: prefer stored, else derive from the card (eurValue / value), else 1.159 fallback
  const fx = useMemo(() => {
    if (detail.fx_gbp_eur) return Number(detail.fx_gbp_eur);
    if (investment?.value && investment?.eurValue) return investment.eurValue / investment.value;
    return 1.159;
  }, [detail.fx_gbp_eur, investment]);

  // ── Derived figures ──
  const heldValueGbp = property.held_value_gbp ?? null;
  const mortgageBalGbp = mortgage.balance_gbp ?? null;
  const netEquityGbp = (heldValueGbp != null && mortgageBalGbp != null)
    ? heldValueGbp - mortgageBalGbp
    : (investment?.value ?? null);
  const netEquityEur = netEquityGbp != null ? netEquityGbp * fx : (investment?.eurValue ?? null);

  const ltvPct = (mortgageBalGbp != null && heldValueGbp)
    ? (mortgageBalGbp / heldValueGbp) * 100
    : null;

  // Monthly payment split: interest = balance × (annualRate/12); capital = payment − interest
  const monthlyInterestGbp = (mortgageBalGbp != null && mortgage.interest_rate_pct != null)
    ? mortgageBalGbp * (mortgage.interest_rate_pct / 100 / 12)
    : null;
  const monthlyCapitalGbp = (mortgage.monthly_payment_gbp != null && monthlyInterestGbp != null)
    ? mortgage.monthly_payment_gbp - monthlyInterestGbp
    : null;

  const annualPaymentGbp = mortgage.monthly_payment_gbp != null
    ? mortgage.monthly_payment_gbp * 12
    : null;

  // Market estimates: prefer the array; fall back to the legacy single-estimate fields.
  const estimates = useMemo(() => {
    if (Array.isArray(property.market_estimates) && property.market_estimates.length) {
      return property.market_estimates;
    }
    if (property.market_estimate_gbp != null) {
      return [{
        source: property.market_estimate_source,
        value_gbp: property.market_estimate_gbp,
        date: property.market_estimate_date,
      }];
    }
    return [];
  }, [property]);

  // ── Chart / KPI from valueHistory ──
  const valueHistory = useMemo(() => investment?.valueHistory || [], [investment]);
  const selectedOption = useMemo(
    () => RANGE_OPTIONS.find(r => r.label === range) || RANGE_OPTIONS[2],
    [range]
  );
  const chartData = useMemo(
    () => filterHistoryToRange(valueHistory, selectedOption),
    [valueHistory, selectedOption]
  );
  const yDomain = useMemo(() => computeYDomain(chartData), [chartData]);
  const periodPerf = useMemo(() => computePerf(valueHistory, selectedOption), [valueHistory, selectedOption]);
  const dailyPerf = useMemo(() => computePerf(valueHistory, RANGE_OPTIONS[0]), [valueHistory]);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content house-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="house-popup-header">
          <h2>Huis — {property.label || 'Residence'}</h2>
          <button className="house-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* ── Account summary bar (net equity) ── */}
        <div className="house-account-bar">
          <div className="house-account-total">
            <span className="house-label">Net Equity</span>
            <span className="house-big-value">{fmtEur(netEquityEur)}</span>
            <span className="house-subvalue">{fmtGbp(netEquityGbp)} @ {fx.toFixed(3)}</span>
          </div>
          <div className="house-account-stat">
            <span className="house-label">Today</span>
            <span className={`house-pnl ${pnlClass(dailyPerf?.abs)}`}>
              {dailyPerf ? fmtEur(dailyPerf.abs, true) : '—'}
              {dailyPerf?.pct != null && <span className="house-pct"> ({fmtPct(dailyPerf.pct)})</span>}
            </span>
          </div>
          <div className="house-account-stat">
            <span className="house-label">{periodKpiLabel(selectedOption)}</span>
            <span className={`house-pnl ${pnlClass(periodPerf?.abs)}`}>
              {periodPerf ? fmtEur(periodPerf.abs, true) : '—'}
              {periodPerf?.pct != null && <span className="house-pct"> ({fmtPct(periodPerf.pct)})</span>}
            </span>
          </div>
        </div>

        {/* ── Performance chart ── */}
        <div className="house-chart-section">
          <div className="house-chart-header">
            <span className="house-section-title">Equity Performance</span>
            <div className="house-range-buttons">
              {RANGE_OPTIONS.map(r => {
                const enabled = hasEnoughHistory(valueHistory, r);
                return (
                  <button
                    key={r.label}
                    className={`house-range-btn ${range === r.label ? 'active' : ''}`}
                    onClick={() => enabled && setRange(r.label)}
                    disabled={!enabled}
                    title={enabled ? '' : 'Not enough history for this range yet'}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={170}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={houseFormatXAxis} tick={{ fontSize: 11 }} minTickGap={40} />
                <YAxis tickFormatter={houseFormatYAxis} tick={{ fontSize: 11 }} width={72} domain={yDomain} allowDataOverflow={false} />
                <Tooltip content={<HouseChartTooltip />} />
                <Line type="monotone" dataKey="eurValue" stroke="#06B6D4" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="house-chart-empty">Not enough history for this range yet.</div>
          )}
        </div>

        {/* ── Equity bridge ── */}
        <div className="house-bridge">
          <div className="house-bridge-item house-bridge-asset">
            <span className="house-bridge-label">Property</span>
            <span className="house-bridge-val">{fmtGbp(heldValueGbp)}</span>
          </div>
          <span className="house-bridge-op">−</span>
          <div className="house-bridge-item house-bridge-liab">
            <span className="house-bridge-label">Mortgage</span>
            <span className="house-bridge-val">{fmtGbp(mortgageBalGbp)}</span>
          </div>
          <span className="house-bridge-op">=</span>
          <div className="house-bridge-item house-bridge-equity">
            <span className="house-bridge-label">Net equity</span>
            <span className="house-bridge-val">{fmtGbp(netEquityGbp)}</span>
          </div>
        </div>

        {/* ── Two component cards ── */}
        <div className="house-cards">
          {/* Property card */}
          <div className="house-card house-card--asset">
            <div className="house-card-title">🏠 Property (asset)</div>
            <div className="house-row">
              <span>Held value</span>
              <strong>{fmtGbp(heldValueGbp)}</strong>
            </div>
            <div className="house-row house-row--note">
              <span>{property.held_basis || 'Conservative basis'}</span>
            </div>
            {estimates.length > 0 && (
              <div className="house-est-block">
                <div className="house-est-head">Market estimates (informational)</div>
                {estimates.map((est, i) => {
                  const vsHeld = (est.value_gbp != null && heldValueGbp)
                    ? ((est.value_gbp - heldValueGbp) / heldValueGbp) * 100
                    : null;
                  const hasRange = est.low_gbp != null && est.high_gbp != null;
                  return (
                    <div key={i} className="house-est">
                      <div className="house-row">
                        <span>{est.source || 'Estimate'}</span>
                        <strong>{fmtGbp(est.value_gbp)}</strong>
                      </div>
                      <div className="house-row house-row--note">
                        <span>
                          {est.date ? formatDateLong(est.date) : ''}
                          {hasRange ? `${est.date ? ' · ' : ''}range ${fmtGbp(est.low_gbp)}–${fmtGbp(est.high_gbp)}` : ''}
                          {vsHeld != null ? ` · ${fmtPct(vsHeld)} vs held` : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="house-card-foot">
              Held at the conservative purchase value; market estimates are informational and do not change the card.
            </div>
          </div>

          {/* Mortgage card */}
          <div className="house-card house-card--liab">
            <div className="house-card-title">🏦 Mortgage (liability)</div>
            <div className="house-row">
              <span>Outstanding balance</span>
              <strong>{fmtGbp(mortgageBalGbp, 2)}</strong>
            </div>
            <div className="house-row">
              <span>Lender / account</span>
              <strong>{mortgage.lender || '—'}{mortgage.account ? ` · ${mortgage.account}` : ''}</strong>
            </div>
            <div className="house-row">
              <span>Loan-to-value</span>
              <strong>{ltvPct != null ? `${ltvPct.toFixed(1)}%` : '—'}</strong>
            </div>
            <div className="house-row">
              <span>Interest rate</span>
              <strong>
                {mortgage.interest_rate_pct != null ? `${mortgage.interest_rate_pct}%` : '—'}
                {mortgage.interest_type ? ` ${mortgage.interest_type}` : ''}
              </strong>
            </div>
            <div className="house-row">
              <span>Fixed until</span>
              <strong>{formatDateLong(mortgage.fixed_until)}</strong>
            </div>
            <div className="house-row">
              <span>Remaining term</span>
              <strong>{monthsToYearsLabel(mortgage.remaining_term_months)}</strong>
            </div>
            <div className="house-row">
              <span>Repayment type</span>
              <strong>{mortgage.repayment_type || '—'}</strong>
            </div>

            <div className="house-pmt">
              <div className="house-pmt-head">
                Monthly payment <strong>{fmtGbp(mortgage.monthly_payment_gbp, 2)}</strong>
                {mortgage.payment_day ? <span className="house-pmt-day"> (due {mortgage.payment_day}th)</span> : null}
              </div>
              <div className="house-pmt-split">
                <div className="house-pmt-seg house-pmt-capital">
                  <span>Capital</span>
                  <strong>{fmtGbp(monthlyCapitalGbp, 0)}</strong>
                </div>
                <div className="house-pmt-seg house-pmt-interest">
                  <span>Interest</span>
                  <strong>{fmtGbp(monthlyInterestGbp, 0)}</strong>
                </div>
              </div>
              {monthlyCapitalGbp != null && mortgage.monthly_payment_gbp ? (
                <div className="house-pmt-bar">
                  <div
                    className="house-pmt-bar-capital"
                    style={{ width: `${(monthlyCapitalGbp / mortgage.monthly_payment_gbp) * 100}%` }}
                  />
                  <div
                    className="house-pmt-bar-interest"
                    style={{ width: `${(monthlyInterestGbp / mortgage.monthly_payment_gbp) * 100}%` }}
                  />
                </div>
              ) : null}
              <div className="house-row house-row--note">
                <span>
                  ≈ {fmtGbp(annualPaymentGbp)}/yr
                  {monthlyCapitalGbp != null ? ` · ${fmtGbp(monthlyCapitalGbp * 12)}/yr equity build` : ''}
                </span>
              </div>
            </div>

            {mortgage.overpayment_allowance_gbp != null && (
              <div className="house-row">
                <span>Overpayment allowance left</span>
                <strong>{fmtGbp(mortgage.overpayment_allowance_gbp, 0)}</strong>
              </div>
            )}
            <div className="house-card-foot">
              Fixed rate ends {formatDateLong(mortgage.fixed_until)} — refinance risk if rates are higher then.
            </div>
          </div>
        </div>

        {detail.as_of && (
          <div className="house-asof">Mortgage data as of {formatDateLong(detail.as_of)} · property valuation {formatDateLong(property.valuation_date) || '—'}</div>
        )}
      </div>
    </div>
  );
}

export default HousePopup;
